import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SignupTravelAgentGQL, SignupTravelAgentRequest, SignupConsumerGQL } from '../../../../graphql';
import { of, interval, Subject, timer } from 'rxjs';
import { takeWhile, takeUntil, take, concatMap, tap, delay, map, catchError } from 'rxjs/operators';
import { Amplitude, RoleManager } from '../../../injectable';
import { WhiteLabelService } from '../../account/white-label-settings/white-label.service';

@Component({
    selector: 'app-account-creation',
    templateUrl: 'account-creation.html'
})
export class AccountCreationComponent {
    loadingMessages: string[] = [
        'Setting up your account...',
        'Configuring preferences...',
        'Loading your dashboard...',
        'Finalizing setup...',
        'This is taking longer than usual, please hold...'
    ];
    funFacts: string[] = [
        'Did you know? We support hundreds of travel agents worldwide.',
        'Tip: You can access your dashboard from any device!',
        'Fun fact: Customize your commissions per booking.',
        'Tip: Book instantly with our one-click booking feature!',
        'Fun fact: We work with 70+ of the world\'s luxury brands.',
        'Tip: You can manage your staff members from the dashboard.',
        'Did you know? We have a 24/7 support team to help you.',
    ];

    currentMessageIndex = 0
    currentFunFactIndex = 0
    setupInProgress = true
    errorMessage: string | null = null
    isWhiteLabelSite = false

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _signupTravelAgent: SignupTravelAgentGQL,
        private _signupConsumer: SignupConsumerGQL,
        private _amplitude: Amplitude,
        private _roleManager: RoleManager,
        private _whiteLabelService: WhiteLabelService
    ) {

        this.isWhiteLabelSite = this._whiteLabelService.isWhiteLabelEnabled()

        const signupRequest: SignupTravelAgentRequest = JSON.parse(decodeURIComponent(this._activatedRoute.snapshot.params.signupRequest))
        
        if (signupRequest) {

            of(true).pipe(
                concatMap(() => this.createAccount$(signupRequest)),
                concatMap(() => this.checkAccountCreation$()),
                tap(() => {
                    this._roleManager.refresh()
                }),
                delay(2000),
                tap(() => {
                    if (this.isWhiteLabelSite) {
                        this._amplitude.logEvent('Consumer Signup Redirected to Platform')
                    } else {
                        this._amplitude.logEvent('User Signup Redirected to Platform')
                    }
                    window.location.href = '/';
                }),
                catchError(error => {
                    console.log('Error while creating account', error)
                    throw error
                })
            ).subscribe()
        }

        // Cycle through loading messages every 4 seconds
        interval(4000).pipe(takeWhile(() => this.setupInProgress)).subscribe(() => {
            if (this.currentMessageIndex == this.loadingMessages.length - 1) {
                this.currentMessageIndex = this.loadingMessages.length - 1
            } else {
                this.currentMessageIndex = (this.currentMessageIndex + 1) % this.loadingMessages.length
            }
        })

        // Cycle through fun facts every 4 seconds
        interval(4000).pipe(takeWhile(() => this.setupInProgress)).subscribe(() => {
            if (this.currentFunFactIndex == this.funFacts.length - 1) {
                this.currentFunFactIndex = this.funFacts.length - 1
            } else {
                this.currentFunFactIndex = (this.currentFunFactIndex + 1) % this.funFacts.length
            }
        })
    }

    private createAccount$(signupRequest) {
        return this.isWhiteLabelSite ? 
            this.signupConsumer(signupRequest) : 
            this.signupTravelAgent(signupRequest)  
    }

    private signupTravelAgent(signupRequest) {
        return this._signupTravelAgent
            .mutate({ input: signupRequest })
            .pipe(
                map((response: any) => {
                    if (response?.errors?.length) {
                        console.log('Error submitting HubSpot form data', JSON.stringify(response.errors))
                        this._amplitude.logEvent('User Signup Failed')
                        const message = 'There was an error creating your account. Please reach out to support at support@reservest.com for assistance.'
                        throw new Error(message)
                    }
                    return true
                })
            )
    }

    private signupConsumer(signupRequest) {
        return this._signupConsumer
            .mutate({ input: signupRequest })
            .pipe(
                map((response: any) => {
                    if (response?.errors?.length) {
                        console.log('Error creating consumer account', JSON.stringify(response.errors))
                        this._amplitude.logEvent('Consumer Signup Failed')
                        const message = 'There was an error creating your account. Please reach out to support@reservest.com for assistance.'
                        throw new Error(message)
                    }
                    return true;
                })
            )
    }

    private checkAccountCreation$(){    
        const subject$ = new Subject()

        timer(500)
            .pipe(
                take(30),
                takeUntil(subject$),
            )
            .subscribe((_) =>
                this._roleManager.queryAccountAsync(true)
                    .subscribe(account => {
                        if (account) {
                            console.log('Account created', account)
                            subject$.next(account)
                            subject$.complete()
                        } else {
                            console.log('Waiting for account creation')
                        }
                    }
                ))

        return subject$
    }
}
